import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useSortable } from '../../hooks/useSortable';
import { usePaginacao } from '../../hooks/usePaginacao';
import { formatDate, formatDateTime } from '../../utils/date';
import { formatMoeda } from '../../utils/fmt';
import StatusBadge from '../../components/common/StatusBadge';
import * as api from '../../api/pedidos';
import * as modelosApi from '../../api/modelos';
import * as carrinhoApi from '../../api/carrinho';
import * as tamanhosApi from '../../api/tamanhos';

function ModalDetalhe({ pedido, modelos, areas, tamanhos, onClose, onCancelado }) {
  const [cancelando, setCancelando] = useState(false);
  const modeloNome = id => modelos.find(m => m.id_modelo_uniforme === id)?.nome || id;
  const areaNome = id => id ? (areas.find(a => a.id_area_cliente === id)?.nome || '—') : null;
  const tamanhoNome = id => tamanhos.find(t => t.id_tamanho_uniforme === id)?.nome || id;
  const totalFab = pedido.itens?.reduce((s, i) => s + i.quantidade_a_fabricar, 0) || 0;

  const cancelar = async () => {
    if (!window.confirm(`Cancelar o pedido ${pedido.numero_pedido}?\n\nSó é possível enquanto a produção ainda não começou.`)) return;
    setCancelando(true);
    try {
      await api.cancelar(pedido.id_pedido);
      onCancelado();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao cancelar pedido.');
    } finally { setCancelando(false); }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title mb-0">
                {pedido.numero_pedido} <StatusBadge status={pedido.status} />
              </h5>
              <small className="text-white-50">
                {pedido.status === 'finalizado'
                  ? `Finalizado em ${formatDateTime(pedido.data_finalizacao)}`
                  : pedido.status === 'cancelado'
                    ? `Cancelado em ${formatDateTime(pedido.data_cancelamento)}`
                    : formatDateTime(pedido.data_pedido)}
              </small>
            </div>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {pedido.endereco_entrega && (
              <div className="alert alert-light py-2 mb-3" style={{ fontSize: 13 }}>
                <strong>Endereço de entrega:</strong> {pedido.endereco_entrega}
                {areaNome(pedido.id_area_cliente) && (
                  <div className="mt-1"><strong>Área:</strong> {areaNome(pedido.id_area_cliente)}</div>
                )}
              </div>
            )}
            {totalFab > 0 && (
              <div className="alert alert-warning py-2 mb-3" style={{ fontSize: 13 }}>
                ⚠ {totalFab} unidade(s) em fila de fabricação — serão produzidas e entregues em seguida.
              </div>
            )}
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Modelo</th><th>Tam.</th><th>Qtd.</th>
                  <th>Preço Unit.</th><th>Subtotal</th><th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {(pedido.itens || []).map(item => (
                  <tr key={item.id_pedido_item}>
                    <td className="fw-semibold">{modeloNome(item.id_modelo_uniforme)}</td>
                    <td><span className="badge bg-secondary">{tamanhoNome(item.id_tamanho_uniforme)}</span></td>
                    <td>{item.quantidade}</td>
                    <td>{formatMoeda(item.preco_unitario_snapshot)}</td>
                    <td className="fw-semibold">{formatMoeda(item.quantidade * item.preco_unitario_snapshot)}</td>
                    <td>
                      {item.quantidade_a_fabricar > 0 ? (
                        <span className="badge bg-warning text-dark" style={{ fontSize: 11 }}>
                          {item.quantidade_pronta_entrega > 0
                            ? `${item.quantidade_pronta_entrega} em estoque + ${item.quantidade_a_fabricar} fab.`
                            : `${item.quantidade_a_fabricar} em fabricação`}
                        </span>
                      ) : (
                        <span className="badge bg-success" style={{ fontSize: 11 }}>Em estoque</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--cjc-gray-50)' }}>
                  <td colSpan={4} className="text-end fw-bold">Total:</td>
                  <td className="fw-bold">{formatMoeda(pedido.valor_total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="modal-footer d-flex justify-content-between">
            {pedido.status === 'aberto' ? (
              <button className="btn btn-outline-danger btn-sm" onClick={cancelar} disabled={cancelando}>
                {cancelando ? 'Cancelando…' : 'Cancelar Pedido'}
              </button>
            ) : <span />}
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MeusPedidos() {
  const [pedidos, setPedidos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [tamanhos, setTamanhos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erroCarregar, setErroCarregar] = useState('');
  const [detalhe, setDetalhe] = useState(null);
  const navigate = useNavigate();

  const carregar = useCallback(() => {
    setLoading(true);
    setErroCarregar('');
    Promise.all([api.listar(), modelosApi.listar(), carrinhoApi.areasDisponiveis(), tamanhosApi.listar()])
      .then(([p, m, a, tam]) => { setPedidos(p.data); setModelos(m.data); setAreas(a.data); setTamanhos(tam.data); })
      .catch(() => setErroCarregar('Erro ao carregar dados. Recarregue a página.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const { sorted, Th } = useSortable(pedidos, 'data_pedido');
  const { paginados, Paginacao } = usePaginacao(sorted);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader titulo="Meus Pedidos" subtitulo={`${pedidos.length} pedido(s)`}>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/catalogo')}>
          Novo Pedido
        </button>
      </PageHeader>

      {erroCarregar && <div className="alert alert-danger py-2">{erroCarregar}</div>}

      {pedidos.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">Você ainda não fez nenhum pedido.</p>
          <button className="btn btn-primary" onClick={() => navigate('/catalogo')}>
            Ver Catálogo
          </button>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <Th campo="numero_pedido">Número</Th>
                      <Th campo="data_pedido">Data</Th>
                      <Th campo="valor_total">Total</Th>
                      <th>Peças</th>
                      <th>Fabricação</th>
                      <th>Status</th>
                      <th style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginados.map(p => {
                      const totalFab = p.itens?.reduce((s, i) => s + i.quantidade_a_fabricar, 0) || 0;
                      return (
                        <tr key={p.id_pedido}>
                          <td className="fw-bold" style={{ color: 'var(--cjc-primary)' }}>{p.numero_pedido}</td>
                          <td>{formatDate(p.data_pedido)}</td>
                          <td className="fw-semibold">{formatMoeda(p.valor_total)}</td>
                          <td>{p.itens?.reduce((s, i) => s + i.quantidade, 0) ?? '—'}</td>
                          <td>
                            {totalFab > 0 ? (
                              <span className="badge bg-warning text-dark" style={{ fontSize: 11 }}>
                                {totalFab} em fab.
                              </span>
                            ) : (
                              <span className="badge bg-success" style={{ fontSize: 11 }}>Em estoque</span>
                            )}
                          </td>
                          <td><StatusBadge status={p.status} /></td>
                          <td>
                            <button className="btn btn-sm btn-outline-primary py-0 px-2"
                              onClick={() => setDetalhe(p)}>Ver</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <Paginacao />
        </>
      )}

      {detalhe && (
        <ModalDetalhe pedido={detalhe} modelos={modelos} areas={areas} tamanhos={tamanhos} onClose={() => setDetalhe(null)}
          onCancelado={() => { setDetalhe(null); carregar(); }} />
      )}
    </div>
  );
}
